import type { ChatPromptScope } from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as chatPromptsApi from "@/lib/api/chat-prompts";

const QUERY_KEY = (scope: ChatPromptScope) => ["chatPrompts", scope] as const;

export function useChatPrompts(scope: ChatPromptScope) {
  const { data, error, isLoading } = useQuery({
    queryKey: QUERY_KEY(scope),
    queryFn: () => chatPromptsApi.listChatPrompts(scope),
  });
  return { prompts: data ?? [], error, isLoading };
}

export function useCreateChatPrompt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: chatPromptsApi.createChatPrompt,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY(created.scope) });
    },
  });
}

export function useUpdateChatPrompt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      title?: string;
      text?: string;
      sortOrder?: number;
    }) => chatPromptsApi.updateChatPrompt(id, data),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY(updated.scope) });
    },
  });
}

export function useDeleteChatPrompt(scope: ChatPromptScope) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: chatPromptsApi.deleteChatPrompt,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY(scope) });
    },
  });
}
